import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    inject,
    input,
    output,
    signal,
    untracked,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { CreateUserStepsApiService, UsersService } from '@core';
import { DEALER_PASSWORD_CHANGE_LIMIT } from '@features/dealers/components/single-dealer/single-dealer.constants';
import { formatCooldownTime } from '@features/dealers/components/single-dealer/single-dealer.utils';
import { DEALER_EDIT_INFORMATION_FORM_FIELDS } from '@features/users/constants';
import { Button, Input, Modal } from '@ntv360/component-pantry';
import { ApiCity, AutocompleteComponent, UiAutocompleteCity } from '@shared';
import { ConfirmationModalComponent } from '@shared/components/modals/confirmation-modal/confirmation-modal.component';
import { formBuilder } from '@shared/utils';
import {
    DealerEditHydration,
    DealerEditSubmitPayload,
} from '../single-dealer/single-dealer.interfaces';

/** Default hydration when no dealer is bound yet */
const EMPTY_HYDRATION: DealerEditHydration = {
    userId: '',
    clientInformationId: '',
    initialEmail: '',
    userMetadata: null,
    firstName: '',
    lastName: '',
    contactPersonPhone: '',
    contactPerson: '',
    clientMetadata: null,
    businessName: '',
    dealerAlias: '',
    dealerId: '',
    city: '',
    state: '',
    region: '',
};

/**
 * @public
 * @description Modal form for editing dealer profile, client information, and triggering change-password.
 */
@Component({
    selector: 'app-edit-dealer-modal',
    standalone: true,
    imports: [
        Modal,
        Button,
        Input,
        ReactiveFormsModule,
        AutocompleteComponent,
        ConfirmationModalComponent,
    ],
    templateUrl: './edit-dealer-modal.component.html',
    styleUrl: './edit-dealer-modal.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EditDealerModalComponent {
    /** Injected form builder for the dealer edit reactive form */
    private readonly fb = inject(FormBuilder);

    /** Users API (reserved for dealer email async validation when re-enabled) */
    private readonly usersService = inject(UsersService);

    /** City search and list endpoints for the location autocomplete */
    private readonly createUserStepsApiService = inject(CreateUserStepsApiService);

    /** User id excluded from email uniqueness checks while the modal is open */
    private readonly excludeEmailUserId = signal<string | undefined>(undefined);

    /**
     * Dealer user id hydrated for the current modal open session.
     * Prevents the hydration effect from re-patching the form (and wiping edits) when
     * `dealer()` receives a new object reference or unrelated signals update.
     */
    private readonly hydratedDealerUserId = signal<string | null>(null);

    /** Bumps when the reactive form mutates so OnPush can refresh submit visibility */
    private readonly formInteractionTick = signal(0);

    /** Reactive form for all editable dealer fields */
    public readonly dealerForm: FormGroup = formBuilder(
        this.fb,
        DEALER_EDIT_INFORMATION_FORM_FIELDS,
        undefined,
        // TODO: Re-enable when dealer email editing is allowed again.
        // [
        //     {
        //         field: 'emailAddress',
        //         asyncValidator: createDealerEditEmailAsyncValidator(this.usersService, () =>
        //             this.excludeEmailUserId(),
        //         ),
        //     },
        // ],
        undefined,
    );

    /** True when the form can be submitted (valid sync/async state and not waiting on validators) */
    protected readonly canSubmitUpdate = computed(() => {
        void this.formInteractionTick();
        return this.dealerForm.valid && !this.dealerForm.pending;
    });

    /** Whether the modal shell is visible */
    public isVisible = input.required<boolean>();

    /** Dealer row data used to hydrate and diff the form */
    public dealer = input<DealerEditHydration>(EMPTY_HYDRATION);

    /** Parent-provided save error message shown above the form */
    public saveError = input<string | null>(null);

    /** True while the parent is deleting this dealer */
    public isDeleting = input<boolean>(false);

    /** Disables the change-password button while cooldown is active */
    public resetPasswordDisabled = input<boolean>(false);

    /** Seconds left for the change-password cooldown */
    public resetPasswordCooldownSeconds = input<number>(0);

    /** Tooltip for the change-password button during cooldown */
    public resetPasswordTooltip = input<string>('');

    /**
     * Human-readable change-password cooldown for the label beside the button
     * (e.g. `3 mins 56 secs`, `56 secs`).
     */
    protected readonly resetPasswordCooldownDisplay = computed(() =>
        formatCooldownTime(this.resetPasswordCooldownSeconds()),
    );

    /** Shows the rate-limit confirmation overlay above this modal */
    public showPasswordLimitReached = input<boolean>(false);

    /** Title for the change-password rate-limit popup */
    public passwordLimitTitle = input<string>(DEALER_PASSWORD_CHANGE_LIMIT.TITLE);

    /** Message for the change-password rate-limit popup */
    public passwordLimitMessage = input<string>(DEALER_PASSWORD_CHANGE_LIMIT.MESSAGE);

    /** Shows the password reset success modal above this modal */
    public showPasswordResetSuccess = input<boolean>(false);

    /** Message for the password reset success modal */
    public passwordResetSuccessMessage = input<string>('');

    /** Title for the password reset success modal */
    public passwordResetSuccessTitle = input<string>('Password Reset Email Sent');

    /** Emitted when the modal requests to close */
    public modalClose = output<void>();

    /** Emitted when the user dismisses the password rate-limit popup */
    public passwordLimitDismiss = output<void>();

    /** Emitted when the user dismisses the password reset success modal */
    public passwordResetSuccessDismiss = output<void>();

    /** Emitted with normalized form values when Update is submitted */
    public update = output<DealerEditSubmitPayload>();

    /** Emitted when Cancel is clicked */
    public cancel = output<void>();

    /** Emitted when Delete Dealer is clicked */
    public delete = output<void>();

    /** Emitted when Change password is clicked (parent runs the API call) */
    public resetPassword = output<void>();

    /** Options for the city autocomplete control */
    protected readonly cityList = signal<UiAutocompleteCity[]>([]);

    /** Loading state for city list / search requests */
    protected readonly isLoadingCities = signal(false);

    /** Controls visibility of the internal delete confirmation modal */
    protected readonly showDeleteConfirm = signal(false);

    /** Template field metadata derived from {@link DEALER_EDIT_INFORMATION_FORM_FIELDS} */
    protected readonly formFields = computed(() =>
        DEALER_EDIT_INFORMATION_FORM_FIELDS.map((fld) => ({
            placeholder: fld.placeholder,
            fieldName: fld.name,
            label: fld.label,
            type: fld.type,
            gridSpan: fld.gridSpan,
        })),
    );

    constructor() {
        this.loadCities();

        const bumpFormInteractionTick = (): void => {
            this.formInteractionTick.update((n) => n + 1);
        };

        this.dealerForm.valueChanges.pipe(takeUntilDestroyed()).subscribe(bumpFormInteractionTick);
        this.dealerForm.statusChanges.pipe(takeUntilDestroyed()).subscribe(bumpFormInteractionTick);

        effect(
            () => {
                if (!this.isVisible()) {
                    this.showDeleteConfirm.set(false);
                    this.hydratedDealerUserId.set(null);
                    return;
                }

                const userId = this.dealer().userId;
                if (!userId || this.hydratedDealerUserId() === userId) {
                    return;
                }

                untracked(() => {
                    const d = this.dealer();
                    this.excludeEmailUserId.set(d.userId);

                    const parts = [d.city, d.state, d.region].map((x) => String(x ?? '').trim());
                    const cityKey = parts.every((p) => !p) ? '' : parts.join('|');

                    const dealerIdCtrl = this.dealerForm.get('dealerId');
                    dealerIdCtrl?.enable({ emitEvent: false });
                    this.dealerForm.patchValue(
                        {
                            firstName: d.firstName,
                            lastName: d.lastName,
                            dealerId: d.dealerId,
                            dealerAlias: d.dealerAlias,
                            businessName: d.businessName,
                            contactPerson: d.contactPerson,
                            contactPersonPhone: d.contactPersonPhone,
                            emailAddress: d.initialEmail,
                            city: cityKey,
                            state: d.state ?? '',
                            region: d.region ?? '',
                        },
                        { emitEvent: false },
                    );
                    dealerIdCtrl?.disable({ emitEvent: false });

                    this.dealerForm.get('emailAddress')?.disable({ emitEvent: false });

                    if (d.city) {
                        this.ensureCityOption(d.city, d.state ?? '', d.region ?? '');
                    }

                    this.hydratedDealerUserId.set(userId);

                    void queueMicrotask(() => {
                        this.dealerForm.markAsPristine();
                        this.dealerForm.updateValueAndValidity();
                        bumpFormInteractionTick();
                    });
                });
            },
            { allowSignalWrites: true },
        );

        effect((onCleanup) => {
            const control = this.dealerForm.get('contactPersonPhone');
            if (!control) {
                return;
            }
            const sub = control.valueChanges.subscribe((value) => {
                const rawValue = String(value ?? '');
                const digitsOnly = rawValue.replace(/\D/g, '');
                const limitedDigits = digitsOnly.slice(0, 10);
                if (rawValue !== limitedDigits) {
                    control.setValue(limitedDigits);
                }
            });
            onCleanup(() => sub.unsubscribe());
        });

        effect((onCleanup) => {
            const cityControl = this.dealerForm.get('city');
            if (!cityControl) {
                return;
            }
            const sub = cityControl.valueChanges.subscribe((val) => {
                if (val != null && val !== '') {
                    return;
                }
                const stateControl = this.dealerForm.get('state');
                const regionControl = this.dealerForm.get('region');
                stateControl?.enable({ emitEvent: false });
                regionControl?.enable({ emitEvent: false });
                this.dealerForm.patchValue({ state: '', region: '' }, { emitEvent: false });
                stateControl?.disable({ emitEvent: false });
                regionControl?.disable({ emitEvent: false });
            });
            onCleanup(() => sub.unsubscribe());
        });
    }

    /**
     * @protected
     * @description Closes the modal via {@link modalClose}
     * @returns void
     */
    protected onClose(): void {
        this.modalClose.emit();
    }

    /**
     * @protected
     * @description Validates the form and emits {@link update} with a {@link DealerEditSubmitPayload}
     * @returns void
     */
    protected onUpdate(): void {
        if (this.dealerForm.pending) {
            return;
        }

        if (this.dealerForm.invalid) {
            this.dealerForm.markAllAsTouched();
            this.dealerForm.updateValueAndValidity();
            this.formInteractionTick.update((n) => n + 1);
            return;
        }

        if (this.dealerForm.pristine) {
            return;
        }

        const raw = this.dealerForm.getRawValue() as Record<string, string>;
        const d = this.dealer();
        this.update.emit({
            userId: d.userId,
            clientInformationId: d.clientInformationId,
            initialEmail: d.initialEmail,
            userMetadata: d.userMetadata,
            firstName: raw['firstName'] ?? '',
            lastName: raw['lastName'] ?? '',
            dealerId: raw['dealerId'] ?? '',
            dealerAlias: raw['dealerAlias'] ?? '',
            businessName: raw['businessName'] ?? '',
            contactPerson: raw['contactPerson'] ?? '',
            contactPersonPhone: raw['contactPersonPhone'] ?? '',
            emailAddress: raw['emailAddress'] ?? '',
            city: raw['city'] ?? '',
            state: raw['state'] ?? '',
            region: raw['region'] ?? '',
            username: d.username,
            auth0UserId: d.auth0UserId,
            statusId: d.statusId,
            organizationIdSignin: d.organizationIdSignin,
            profilePicture: d.profilePicture,
        });
    }

    /**
     * @protected
     * @description Emits {@link cancel} without persisting changes
     * @returns void
     */
    protected onCancel(): void {
        this.cancel.emit();
    }

    /**
     * @protected
     * @description Shows the internal delete confirmation modal
     * @returns void
     */
    protected onDelete(): void {
        this.showDeleteConfirm.set(true);
    }

    /**
     * @protected
     * @description Emits {@link delete} so the parent can call the API
     * @returns void
     */
    protected onConfirmDelete(): void {
        this.delete.emit();
    }

    /**
     * @protected
     * @description Closes the internal delete confirmation modal
     * @returns void
     */
    protected onCloseDeleteConfirm(): void {
        this.showDeleteConfirm.set(false);
    }

    /**
     * @protected
     * @description Emits {@link resetPassword}; parent performs force-password-change
     * @returns void
     */
    protected onResetPassword(): void {
        this.resetPassword.emit();
    }

    /**
     * @protected
     * @description Dismisses the change-password rate-limit overlay via {@link passwordLimitDismiss}
     * @returns void
     */
    protected onDismissPasswordLimit(): void {
        this.passwordLimitDismiss.emit();
    }

    /**
     * @protected
     * @description Dismisses the password reset success overlay via {@link passwordResetSuccessDismiss}
     * @returns void
     */
    protected onDismissPasswordResetSuccess(): void {
        this.passwordResetSuccessDismiss.emit();
    }

    /**
     * @protected
     * @description Whether state/region inputs are read-only (filled from city selection)
     * @param fieldName - Form control name
     * @returns True for `state` and `region`
     */
    protected isReadOnlyProfileLocationField(fieldName: string): boolean {
        return fieldName === 'state' || fieldName === 'region';
    }

    /**
     * @protected
     * @description Handles city autocomplete search; reloads full list when query is empty
     * @param search - Raw search string from the autocomplete
     * @returns void
     */
    protected onSearch(search: string): void {
        const normalizedSearch = search.trim();
        if (!normalizedSearch) {
            this.loadCities();
            return;
        }
        this.searchCity(normalizedSearch);
    }

    /**
     * @protected
     * @description Applies city, state, and region when a location option is selected
     * @param option - Selected autocomplete row, or null
     * @returns void
     */
    protected onSelect(option: UiAutocompleteCity | null): void {
        if (!option) {
            return;
        }
        const stateValue = String(option.stateName ?? option.state ?? '').trim();
        this.dealerForm.patchValue(
            {
                city: option.selectionKey,
                state: stateValue,
                region: option.region,
            },
            { emitEvent: true },
        );
        this.dealerForm.markAsDirty();
    }

    /**
     * @protected
     * @description Restricts contact-number fields to digits and a maximum of 10 characters
     * @param event - Keydown from the input
     * @param fieldName - Form control name
     * @returns void
     */
    protected onNumericKeydown(event: KeyboardEvent, fieldName: string): void {
        if (!this.isContactNumberField(fieldName)) {
            return;
        }
        const allowedControlKeys = [
            'Backspace',
            'Delete',
            'Tab',
            'ArrowLeft',
            'ArrowRight',
            'Home',
            'End',
        ];
        if (allowedControlKeys.includes(event.key)) {
            return;
        }
        if (event.ctrlKey || event.metaKey) {
            return;
        }
        if (!/^\d$/.test(event.key)) {
            event.preventDefault();
            return;
        }
        const currentValue = String(this.dealerForm.get(fieldName)?.value ?? '');
        if (currentValue.replace(/\D/g, '').length >= 10) {
            event.preventDefault();
        }
    }

    /**
     * @protected
     * @description Returns true when field should show validation errors while typing (dirty)
     * @param fieldName - The name of the form field
     * @returns boolean
     */
    protected isFieldWithLiveValidation(fieldName: string): boolean {
        return ['contactPersonPhone', 'emailAddress'].includes(fieldName);
    }

    /**
     * @protected
     * @description Whether the field is the dealer contact phone control
     * @param fieldName - Form control name
     * @returns True when `fieldName` is `contactPersonPhone`
     */
    protected isContactNumberField(fieldName: string): boolean {
        return fieldName === 'contactPersonPhone';
    }

    /**
     * @protected
     * @description Resolves user-facing validation messages for a control
     * @param fieldName - Form control name
     * @returns Array of error strings (empty when nothing to show)
     */
    protected getFieldErrorMessage(fieldName: string): string[] {
        const control = this.dealerForm.get(fieldName);
        if (!control) {
            return [];
        }
        const shouldShowWhileTyping =
            this.isFieldWithLiveValidation(fieldName) && (control.dirty || control.touched);
        if (!shouldShowWhileTyping && !control.touched) {
            return [];
        }
        const field = DEALER_EDIT_INFORMATION_FORM_FIELDS.find((f) => f.name === fieldName);
        if (!field?.errors) {
            return [];
        }
        return Object.keys(control.errors ?? {})
            .map((errorKey) => field.errors![errorKey])
            .filter(Boolean);
    }

    /**
     * @private
     * @description Ensures the hydrated city exists in {@link cityList} for the autocomplete display
     * @param city - Plain city name from the API
     * @param state - State label or abbreviation
     * @param region - Region code
     * @returns void
     */
    private ensureCityOption(city: string, state: string, region: string): void {
        const selectionKey = [city, state, region].map((x) => String(x ?? '').trim()).join('|');
        const list = this.cityList();
        if (list.some((o) => o.selectionKey === selectionKey)) {
            return;
        }
        const displayState = state.trim();
        const label = displayState ? `${city}, ${displayState}` : city;
        this.cityList.update((xs) => [
            {
                city,
                country: '',
                state,
                stateName: displayState,
                zipCode: '',
                region,
                label,
                value: selectionKey,
                selectionKey,
            },
            ...xs,
        ]);
    }

    /**
     * @private
     * @description Maps API city rows to autocomplete options with composite `selectionKey`
     * @param data - Cities from the create-user steps API
     * @returns UI options for {@link AutocompleteComponent}
     */
    private mapDataToUI(data: ApiCity[]): UiAutocompleteCity[] {
        return data.map((item) => {
            const displayState = (item.stateName ?? item.state)?.trim() ?? '';
            const label = displayState ? `${item.city}, ${displayState}` : item.city;
            const selectionKey = [item.city, item.state, item.region].join('|');
            return {
                city: item.city,
                country: item.country,
                state: item.state,
                stateName: item.stateName,
                zipCode: item.zipCode,
                region: item.region,
                label,
                value: selectionKey,
                selectionKey,
            };
        });
    }

    /**
     * @private
     * @description Loads the default city list when the modal is constructed
     * @returns void
     */
    private loadCities(): void {
        this.isLoadingCities.set(true);
        this.createUserStepsApiService.getCities().subscribe({
            next: (cities) => {
                this.cityList.set(this.mapDataToUI(cities));
                this.isLoadingCities.set(false);
            },
            error: (error: unknown) => {
                console.error('Error fetching cities: ', error);
                this.isLoadingCities.set(false);
            },
        });
    }

    /**
     * @private
     * @description Searches cities by query string for the autocomplete
     * @param search - Trimmed search text (min length enforced by the control)
     * @returns void
     */
    private searchCity(search: string): void {
        this.isLoadingCities.set(true);
        this.createUserStepsApiService.searchCity(search).subscribe({
            next: (cities) => {
                this.cityList.set(this.mapDataToUI(cities));
                this.isLoadingCities.set(false);
            },
            error: (error: unknown) => {
                console.error('Error fetching cities: ', error);
                this.isLoadingCities.set(false);
            },
        });
    }
}
